/** The stage as three activities describe it, read into what a page can draw. */

import { attribute, currentAttribute, has, type CanvasState, type StateEntity } from "../../lib/runtime";

const AVATAR = "activity.avatar_stage.avatar";
const STAGE_EMOTE = "activity.avatar_stage.emote";
const EMOTING = "activity.avatar_stage.emoting";
const ENTRANT = "activity.drop_in.entrant";
const BAR_EMOTE = "activity.emote_bar.emote";

/** The eight directions, clockwise from north. `avatar.facing` is an index into this. */
export const DIRECTIONS = [
  "north",
  "north_east",
  "east",
  "south_east",
  "south",
  "south_west",
  "west",
  "north_west",
] as const;

export type Direction = (typeof DIRECTIONS)[number];

const DEAD_ZONE = 0.28;

/** Which of the eight a pull from a pad's centre points at, or null inside the dead zone. */
export function directionAt(dx: number, dy: number, radius: number): Direction | null {
  if (Math.hypot(dx, dy) / radius < DEAD_ZONE) return null;
  const clockwiseFromNorth = (Math.atan2(dx, -dy) * 180) / Math.PI;
  const slot = Math.round(((clockwiseFromNorth + 360) % 360) / 45) % 8;
  return DIRECTIONS[slot] ?? null;
}

export interface Avatar {
  /** The participant id, which is also the entity id and what a stop is addressed to. */
  readonly id: string;
  /** Normalized display space, so a body is where it is on any monitor. */
  readonly x: number;
  readonly y: number;
  /** An index into `DIRECTIONS`. */
  readonly facing: number;
  /** 1-based into the configured emote list, or 0 when nothing is playing. */
  readonly emote: number;
  readonly emoting: boolean;
}

/** Every body on the stage, ordered by id so a redraw never reshuffles them. */
export function avatarsOf(stage: CanvasState | null): Avatar[] {
  return (stage?.entities ?? [])
    .filter((entity) => has(entity, AVATAR))
    .map((entity) => ({
      id: entity.id,
      x: currentAttribute(entity, "x"),
      y: currentAttribute(entity, "y"),
      facing: currentAttribute(entity, "facing"),
      emote: currentAttribute(entity, "emote"),
      emoting: has(entity, EMOTING),
    }))
    .sort((one, other) => one.id.localeCompare(other.id));
}

/** The ids of everyone currently taking part, whether or not they have a body yet. */
export function entrantsOf(roster: CanvasState | null): string[] {
  return (roster?.entities ?? [])
    .filter((entity: StateEntity) => has(entity, ENTRANT))
    .map((entity) => entity.id)
    .sort();
}

/** The emote ids the bar is offering, in the order it holds them. */
export function barEmotesOf(bar: CanvasState | null): string[] {
  return (bar?.entities ?? [])
    .filter((entity: StateEntity) => has(entity, BAR_EMOTE))
    .map((entity) => entity.id)
    .sort();
}

/** The stage's own emote set, indexed by the slot `avatar.emote` names. */
export function slotsOf(stage: CanvasState | null): Map<number, string> {
  const slots = new Map<number, string>();
  for (const entity of stage?.entities ?? []) {
    if (!has(entity, STAGE_EMOTE)) continue;
    slots.set(Math.round(attribute(entity, "slot")), entity.id);
  }
  return slots;
}

/** A participant id shortened to something a moderator can read at a glance. */
export function shortId(id: string): string {
  return id.length <= 8 ? id : id.slice(0, 8);
}

/** A stable colour per body, hashed from the id rather than its place in the list. */
export function colourOf(id: string): string {
  let hash = 0;
  for (const character of id) hash = (hash * 31 + character.charCodeAt(0)) % 360;
  return `hsl(${hash} 70% 60%)`;
}

/** Which emote a body is playing, or null when none is. */
export function playingEmote(avatar: Avatar, slots: Map<number, string>): string | null {
  if (!avatar.emoting || avatar.emote < 1) return null;
  return slots.get(Math.round(avatar.emote)) ?? null;
}
